// src/generators/dzial5_trygonometria/temat2_funkcje_rozszerzone/podtemat2_wzory_redukcyjne_types/ReduceTanExactTask.js
import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import RootFractionInput, { parseRootFractionInput, formatLatexPreview } from "../../../../components/inputs/RootFractionInput";

// ===== helpers =====
function tanValueLatex(angle) {
  const map = {
    0: "0",
    30: "\\frac{\\sqrt{3}}{3}",
    45: "1",
    60: "\\sqrt{3}",
    90: "\\text{nie~określony}"
  };
  return map[angle];
}

function expectedNumeric(acuteAngle, alpha) {
  if (alpha === 180) return 0;
  switch (acuteAngle) {
    case 0:  return 0;
    case 30: return -Math.sqrt(3) / 3;
    case 45: return -1;
    case 60: return -Math.sqrt(3);
    case 90: return NaN; // nieokreślony
    default: return NaN;
  }
}

function normalizeLatex(str) {
  return String(str)
    .replace(/\s/g, "")
    .replace(/\*/g, "")
    .replace(/sqrt\(([^)]+)\)/g, "√$1")
    .replace(/\\sqrt{([^}]+)}/g, "√$1")
    .replace(/\\dfrac{([^}]+)}{([^}]+)}/g, "$1/$2")
    .replace(/\\frac{([^}]+)}{([^}]+)}/g, "$1/$2")
    .replace(/[()]/g, "")
    .replace(/\\/g, "");
}

// ===== task =====
export function generateTask() {
  const angles = [120, 135, 150, 180];
  const alpha = angles[Math.floor(Math.random() * angles.length)];

  let acuteAngle, exactValue;
  if (alpha === 180) {
    acuteAngle = 0;
    exactValue = "0";
  } else {
    acuteAngle = 180 - alpha;
    const base = tanValueLatex(acuteAngle);
    exactValue = base === "0" ? "0" : `-${base}`;
  }

  return {
    type: "ReduceTanExactTask",
    data: { alpha, acuteAngle, exactValue }
  };
}

export function renderTask({ data }) {
  const { alpha } = data;
  return (
    <div style={{ marginBottom: "1rem" }}>
      Zredukuj kąt i oblicz dokładną wartość:
      <InlineMath math={`\\ \\tan ${alpha}^\\circ`} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={`\\tan ${task?.data?.alpha}^\\circ`}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const { alpha, acuteAngle, exactValue } = data;

  // meta do dynamicznego kąta w renderUserAnswer
  if (typeof userAnswer === "object") {
    userAnswer.func = "tan";
    userAnswer.angle = alpha;
  }

  // 1) numerycznie
  const expectedValue = expectedNumeric(acuteAngle, alpha);
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  if (!Number.isNaN(expectedValue) && parsed != null) {
    if (Math.abs(parsed - expectedValue) < 0.01) return true;
  }

  // 2) latex/tekst
  if (!raw) return false;
  return normalizeLatex(raw) === normalizeLatex(exactValue);
}

export function renderAnswer({ data }) {
  const { alpha, exactValue } = data;
  return (
    <InlineMath math={`\\displaystyle \\tan ${alpha}^\\circ = ${exactValue}`} />
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const angle = typeof userAnswer === "object" ? userAnswer.angle : undefined;

  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  const dynamicLabel = angle != null ? `\\tan ${angle}^\\circ` : "\\tan\\alpha";
  return (
    <InlineMath math={`\\displaystyle ${formatLatexPreview(raw, dynamicLabel)}`} />
  );
}

export function renderExplanation({ data }) {
  const { alpha, acuteAngle, exactValue } = data;
  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Korzystamy ze wzoru redukcyjnego:
        <BlockMath math={`\\tan(180^\\circ - \\alpha) = -\\tan \\alpha`} />
      </div>

      <div>
        <strong>Krok 2:</strong> Podstawiamy <InlineMath math={`\\alpha = ${alpha}^\\circ`} />:
        <BlockMath math={`\\tan ${alpha}^\\circ = -\\tan(180^\\circ - ${alpha}^\\circ)`} />
        <BlockMath math={`\\tan ${alpha}^\\circ = -\\tan ${acuteAngle}^\\circ`} />
      </div>

      <div>
        <strong>Krok 3:</strong> Odczytujemy dokładną wartość:
        <BlockMath math={`\\tan ${acuteAngle}^\\circ = ${exactValue}`} />
      </div>
    </div>
  );
}
